import React, { useState } from 'react';
import { Typography } from 'lib/components';
import { Checkbox } from 'lib/components/FormFields';
import { JobCustomCost } from 'types';
import classes from './index.module.scss';

/**
 * Props interface for the JobCustomCostList component
 */
interface Props {
	/** Array of custom cost items to display */
	items: Array<JobCustomCost>;
	/** Callback function when a cost item is checked/unchecked */
	updateCustomCosts: (cost: JobCustomCost, checked: boolean) => void;
	/** Optional array of custom cost IDs that should be checked by default */
	defaultCheckedIds?: Array<number>;
}

/**
 * Props interface for the JobCustomCostItem component
 */
interface ItemProps {
	/** Custom cost item data */
	item: JobCustomCost;
	/** Callback function when the item is checked/unchecked */
	updateCustomCosts: (cost: JobCustomCost, checked: boolean) => void;
	/** Whether the item should be checked by default */
	defaultChecked?: boolean;
}

/**
 * JobCustomCostItem component
 * Renders a single custom cost item with a checkbox
 * Manages its own checked state and calls the update callback when changed
 *
 * @param {ItemProps} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const JobCustomCostItem = ({ item, updateCustomCosts, defaultChecked = false }: ItemProps) => {
	// Local state for checkbox
	const [checked, setChecked] = useState(defaultChecked);

	/**
	 * Handle checkbox change event
	 * Updates local state and calls the parent callback
	 *
	 * @param {React.ChangeEvent<HTMLInputElement>} e - Change event
	 */
	const handleChecked = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.checked;
		setChecked(value);
		updateCustomCosts(item, value);
	};

	return (
		<div key={item.customCostId} className={classes['costAmplifiers-listItem']}>
			{/* Cost item name */}
			<Typography>{item.name}</Typography>
			{/* Checkbox for selecting the cost item */}
			<Checkbox checked={checked} onChange={handleChecked} />
		</div>
	);
};

/**
 * JobCustomCostList component
 * Renders a list of custom cost items with checkboxes
 * Maps through items and renders a JobCustomCostItem for each
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const JobCustomCostList = ({ items, updateCustomCosts, defaultCheckedIds }: Props) => {
	return (
		<div className={classes['costAmplifiers-list']}>
			{/* Map through items and render a JobCustomCostItem for each */}
			{items.map((item) => (
				<JobCustomCostItem
					key={item.customCostId}
					item={item}
					updateCustomCosts={updateCustomCosts}
					// Check if this item's ID is in the defaultCheckedIds array
					defaultChecked={defaultCheckedIds?.includes(item.customCostId)}
				/>
			))}
		</div>
	);
};

export default JobCustomCostList;
